import { styled } from "goober";
import { type ReactNode, useState } from "react";
import { playClick } from "../lib/sounds";
import { Button } from "./Button";
import { Checkbox } from "./Checkbox";
import { ClickButton } from "./ClickButton";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { Text } from "./Text";
import { ToggleVisual } from "./Toggle";

const Content = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;
`;

const TitleBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const List = styled("div")`
  margin: 0;
  padding: 4px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  background: var(--surface-secondary);
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  min-width: 0;

  &:hover {
    background: var(--alpha4);
  }

  & .arrow-icon {
    opacity: 0;
    flex-shrink: 0;
    display: flex;
  }

  &:hover .arrow-icon,
  &:focus-within .arrow-icon {
    opacity: 0.5;
  }
`;

const RowLink = styled("a")`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const SelectBadge = styled(ClickButton)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 32px;
  margin: -4px;

  .item-badge {
    display: flex;
  }

  .item-checkbox {
    position: absolute;
    display: flex;
    opacity: 0;
  }

  ${Row}:hover & .item-badge,
  &:focus-visible .item-badge,
  &[aria-checked="true"] .item-badge {
    opacity: 0;
  }

  ${Row}:hover & .item-checkbox,
  &:focus-visible .item-checkbox {
    opacity: 0.3;
  }

  &[aria-checked="true"] .item-checkbox {
    opacity: 1;
  }

  @media (hover: none) {
    .item-badge {
      opacity: 0;
    }
    .item-checkbox {
      opacity: 0.3;
    }
  }
`;

const BadgeSlot = styled("span")`
  display: flex;
  flex-shrink: 0;
`;

const RowText = styled("div")`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

type TidyUpItem = {
  key: string;
  href: string;
  badge: ReactNode;
  name: string;
  subtitle?: string;
  createdByUserId?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (keys: string[]) => void;
  itemNoun: { singular: string; plural: string };
  inactivityDays: number;
  // Optional line above the list, e.g. a warning that removal also deletes something.
  summary?: ReactNode;
  items: TidyUpItem[];
  selectable?: boolean;
  currentUserId?: string;
};

export function TidyUpModal({ open, onClose, ...props }: Props) {
  return (
    <Modal open={open} onClose={onClose}>
      <TidyUpContent {...props} />
    </Modal>
  );
}

function TidyUpContent({
  onConfirm,
  itemNoun,
  inactivityDays,
  summary,
  items,
  selectable = false,
  currentUserId,
}: Omit<Props, "open" | "onClose">) {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [onlyMine, setOnlyMine] = useState(false);
  const visibleItems = items.filter(
    (item) => !onlyMine || item.createdByUserId === currentUserId
  );
  const selectedItems = visibleItems.filter((item) =>
    selectedKeys.includes(item.key)
  );
  const itemsToDelete =
    selectable && selectedItems.length > 0 ? selectedItems : visibleItems;
  const count = items.length;
  const noun = count === 1 ? itemNoun.singular : itemNoun.plural;
  const description =
    count > 0
      ? `You have ${count} ${noun} with no chats or runs in the last ${inactivityDays} days. Remove ${
          count === 1 ? "it" : "them"
        } to keep things tidy.`
      : `Nothing to tidy — all your ${itemNoun.plural} have been active in the last ${inactivityDays} days. You’re already on top of things.`;
  const deleteLabel = selectable
    ? selectedItems.length > 0
      ? `Delete ${selectedItems.length} ${selectedItems.length === 1 ? itemNoun.singular : itemNoun.plural}`
      : "Delete all"
    : count === 1
      ? "Delete"
      : "Delete all";

  return (
    <Content>
      <TitleBlock>
        <Text variant="text-regular-md" color="var(--text-primary)">
          Tidy up
        </Text>
        <Text variant="text-regular" color="var(--text-secondary)">
          {description}
        </Text>
      </TitleBlock>
      {items.length > 0 && (
        <Body>
          {summary}
          <List style={selectable ? { height: 240 } : undefined}>
            {visibleItems.length === 0 && (
              <Text
                variant="text-small"
                color="var(--text-secondary)"
                style={{ padding: 8 }}
              >
                None of your {itemNoun.plural} need tidying up.
              </Text>
            )}
            {visibleItems.map((item) => (
              <Row key={item.key}>
                {selectable && (
                  <SelectBadge
                    role="checkbox"
                    aria-label={`Select ${item.name}`}
                    aria-checked={selectedKeys.includes(item.key)}
                    onClick={() =>
                      setSelectedKeys((keys) =>
                        keys.includes(item.key)
                          ? keys.filter((key) => key !== item.key)
                          : [...keys, item.key]
                      )
                    }
                  >
                    <span className="item-badge">{item.badge}</span>
                    <span className="item-checkbox">
                      <Checkbox checked={selectedKeys.includes(item.key)} />
                    </span>
                  </SelectBadge>
                )}
                <RowLink
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onPointerDown={(event) => {
                    if (event.button === 0) playClick();
                  }}
                  onClick={(event) => {
                    if (event.detail === 0) playClick();
                  }}
                >
                  {!selectable && <BadgeSlot>{item.badge}</BadgeSlot>}
                  <RowText>
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {item.name}
                    </Text>
                    {item.subtitle && (
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {item.subtitle}
                      </Text>
                    )}
                  </RowText>
                  <span className="arrow-icon">
                    <Icon name="arrow-up-right" size={12} />
                  </span>
                </RowLink>
              </Row>
            ))}
          </List>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 16,
            }}
          >
            {selectable && currentUserId && (
              <ClickButton
                role="switch"
                aria-checked={onlyMine}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginLeft: 8,
                  marginRight: "auto",
                }}
                onClick={() => {
                  setOnlyMine(!onlyMine);
                  setSelectedKeys(
                    selectedItems
                      .filter(
                        (item) =>
                          onlyMine || item.createdByUserId === currentUserId
                      )
                      .map((item) => item.key)
                  );
                }}
              >
                <ToggleVisual isOn={onlyMine} />
                <Text variant="text-small" color="var(--text-secondary)">
                  Only mine
                </Text>
              </ClickButton>
            )}
            <Button
              size="l"
              variant="danger"
              disabled={itemsToDelete.length === 0}
              onClick={() => onConfirm(itemsToDelete.map((item) => item.key))}
            >
              {deleteLabel}
            </Button>
          </div>
        </Body>
      )}
    </Content>
  );
}
